import {
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import {get, isEmpty, join, map, toNumber} from 'lodash';
import {FC, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useGetServiceRequestsQuery} from '../../../../global/services/service-requests';
import {BASE_TITLES} from '../../../../shared/constants';
import {DateHelpers} from '../../../../shared/helpers/dateHelpers';
import {getInitials} from '../../../../shared/helpers/getFullName';
import {useRedirectedContractsStore} from '../../../../store/redirectedContracts';
import {
  changeContractRequestTablePage,
  changeContractRequestTableRowsPerPage,
  useContractRequestStore,
} from '../../stores/contract-request.store';

export const ContractRequestsTable: FC = () => {
  const {id: paramsId} = useParams();
  const navigate = useNavigate();
  const {addContract} = useRedirectedContractsStore();
  const {data: state} = useContractRequestStore();
  const {data, loading} = useGetServiceRequestsQuery({
    counter: state.counter,
    size: state.rowsPerPage,
    page: state.page,
    archive: true,
    // counter отсутствует при перезагрузке страницы, поэтому добавлен contractId
    contractId: paramsId ? Number(paramsId) : undefined,
    ...(state.id && {id: toNumber(state.id)}),
  });
  const requests = get(data, 'content', []);
  const showEmptyContent = isEmpty(requests);

  const handleRowClick = (id?: number) => {
    if (!id) return;
    addContract(paramsId as string, id.toString());
    navigate('/serviceRequests/' + id, {state: {contract: paramsId}});
  };

  return (
    <TableContainer>
      <Table sx={{minWidth: 650}} size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center">{BASE_TITLES.id.base}</TableCell>
            <TableCell align="center">{BASE_TITLES.company.base}</TableCell>
            <TableCell align="center">{BASE_TITLES.contract.base}</TableCell>
            <TableCell align="center">{BASE_TITLES.manager.base}</TableCell>
            <TableCell align="center">{BASE_TITLES.workers.base}</TableCell>
            <TableCell align="center">{BASE_TITLES.address.base}</TableCell>
            <TableCell align="center">{BASE_TITLES.taskLocation.base}</TableCell>
            <TableCell align="center">{BASE_TITLES.createdDate.base}</TableCell>
            <TableCell align="center">{BASE_TITLES.executedDate.base}</TableCell>
            <TableCell align="center">{BASE_TITLES.viewRequest.base}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {requests.map((item) => (
            <TableRow key={item.id}>
              <TableCell align="center">{item.id}</TableCell>
              <TableCell align="center">{item.companyName}</TableCell>
              <TableCell align="center">{item?.contract?.description || '-'}</TableCell>
              <TableCell align="center">{item.manager ? getInitials(item.manager) : '-'}</TableCell>
              <TableCell align="center">{item.workers ? join(map(item.workers, getInitials), ', ') : '-'}</TableCell>
              <TableCell align="center">{item.address?.fullAddress || '-'}</TableCell>
              <TableCell align="center">{item.taskLocation ? item.taskLocation.name : '-'}</TableCell>
              <TableCell align="center">{DateHelpers.formatToRu(item.createdDate)}</TableCell>
              <TableCell align="center">{DateHelpers.formatToRu(item.executedDate)}</TableCell>
              <TableCell align="center">
                <Button variant="outlined" size="small" onClick={() => handleRowClick(item.id)}>
                  Перейти к заявке
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {renderLoadingOrEmptyRow(loading, showEmptyContent)}
        </TableBody>
      </Table>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 100]}
        component="div"
        count={get(data, 'totalElements', 0)}
        rowsPerPage={state.rowsPerPage}
        page={state.page}
        onPageChange={changeContractRequestTablePage}
        onRowsPerPageChange={changeContractRequestTableRowsPerPage}
      />
    </TableContainer>
  );
};

const CONTENT = [<CircularProgress />, 'Заявки не найдены'] as const;

function renderLoadingOrEmptyRow(loading: boolean, showEmptyContent: boolean) {
  if (!loading && !showEmptyContent) return null;

  return (
    <TableRow>
      <TableCell align="center" colSpan={10}>
        <Stack minHeight={300} justifyContent="center" alignItems="center">
          {CONTENT[toNumber(showEmptyContent && !loading)]}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
